/**
 * RefereeSignal frame: server→client "the referee just whistled / showed a card".
 * (§5.6, §2.8)
 *
 * ── Why this exists at all, when `room.ts drainEvents` declined to put rule
 *    events on the wire ──────────────────────────────────────────────────────
 *
 * That objection was RIGHT and is respected here. Verbatim, it was: *"everything
 * a client needs to render a goal, a card or an offside is already in the
 * snapshot's match state (score, phase, restart, pendingFoul). Adding an event
 * channel would be a second source of truth for things the snapshot already
 * carries (§2.8)."* Duplicating STATE on the wire is exactly how two sources of
 * truth start disagreeing, and a goal really is reconstructible: the score
 * changed.
 *
 * A whistle and a card-shown are not state. They are EDGES, and they are not
 * reconstructible from any snapshot:
 *
 *   • THE WHISTLE happens on the restart's `Setup → Ready` flip (rules/fsm.ts).
 *     `restartStage` is on the snapshot, but snapshots are sent at
 *     SNAPSHOT_SEND_RATE (20Hz) and delta-compressed, so a stage that flips and
 *     is gone inside one snapshot interval is INVISIBLE — and Ready lapses into
 *     Live the moment the taker strikes. The client cannot see the moment; it
 *     can only see that it has passed. That is precisely why the client's audio
 *     layer had to infer a whistle from a Kickoff→InPlay phase edge, which fires
 *     when the ball is struck (a whole Ready window late) and never fired at all
 *     for a throw-in, corner, goal kick, free kick or penalty. The owner
 *     reported that as a bug: *"the ball is in game only after refree whisely
 *     for start off , throwin , foul , corner"*.
 *   • THE CARD is shown ONCE, at the offence (`fouls.ts applyCard`). What the
 *     snapshot carries afterwards is the CONSEQUENCE — `yellows`, `sentOff` — and
 *     a delta only reports a field that changed *between the two frames it
 *     spans*, so the instant of the booking has no representation. Diffing
 *     `pendingFoul` doesn't help: a straight red skips advantage entirely and
 *     never populates it.
 *
 * So this frame carries no state. It carries "this happened, at this tick, at
 * this spot", the client shows a cue and forgets it, and nothing here is ever fed
 * back into a WorldState. If it is dropped, the match is bit-identical — the sim
 * is still the only authority (§15.2).
 *
 * ── Why a dedicated tiny frame, not a snapshot field ────────────────────────
 *
 * The same reasoning control.ts already documents for ControlAssign: *"a switch
 * is an event, not per-tick state, and folding it into the 20Hz snapshot would
 * either delay the handoff by up to a snapshot interval or force a field onto
 * every snapshot that is set on almost none of them."* A whistle fires a handful
 * of times a minute. 10 bytes each, on the tick it happens.
 *
 * ── Layout (payload only; the MessageType byte is added by protocol.ts) ──────
 *
 *   kind   [REF_SIGNAL_KIND_BITS = 3]  RefSignalWire; 0 is invalid
 *   phase  [WIRE_PHASE_BITS      = 4]  MatchPhase being whistled (Warmup for a card)
 *   player [WIRE_PLAYER_REF_BITS = 6]  taker / offender, 0 = none
 *   team   [WIRE_TEAM_REF_BITS   = 2]  side it favours, 0 = none
 *   x      [QUANT_POS_BITS      = 16]  the spot, quantized like any position
 *   z      [QUANT_POS_BITS      = 16]
 *   tick   [WIRE_TICK_BITS      = 32]  the sim tick it happened on
 *
 * 79 bits → 10 bytes of payload, 11 on the wire.
 *
 * CHANGING THIS LAYOUT IS A PROTOCOL BREAK. Bump PROTOCOL_VERSION (§12.6).
 */

import { MatchPhase } from '../sim/types.js';
import { QUANT_POS_BITS } from '../sim/constants.js';
import { BitReader, BitWriter } from './bitwriter.js';
import {
  WIRE_PHASE_BITS,
  WIRE_PLAYER_REF_BITS,
  WIRE_TEAM_REF_BITS,
  WIRE_TICK_BITS,
  decodePlayerRef,
  decodeTeamRef,
  dequantizePosX,
  dequantizePosZ,
  encodePlayerRef,
  encodeTeamRef,
  quantizePosX,
  quantizePosZ,
} from './serialize.js';

/**
 * What the referee is signalling.
 *
 * THESE INTEGERS ARE SHARED WITH THE CLIENT'S RENDER LAYER: `RefSignalKind` in
 * packages/client/src/render/refSignal.ts uses the same values so an online
 * signal and an offline one drive one channel. `refSignal.test.ts` asserts the
 * two agree — renumbering one without the other fails that test rather than
 * silently showing a red card for a throw-in.
 *
 * 3 bits, so there is room for a future signal (an advantage wave, a VAR check)
 * without another layout change.
 */
export const enum RefSignalWire {
  /** 0 is deliberately not a signal — a zeroed/corrupt frame decodes as invalid. */
  Invalid = 0,
  Whistle = 1,
  Yellow = 2,
  Red = 3,
}

export const REF_SIGNAL_KIND_BITS = 3;

/** Decoded payload. Allocated once by `createRefSignalFrame` and reused. */
export interface RefSignalFrame {
  kind: RefSignalWire;
  /** The restart being whistled; `MatchPhase.Warmup` when this is a card. */
  phase: MatchPhase;
  /** Designated taker / offender, or -1. */
  player: number;
  /** Side the decision favours, or -1. */
  team: number;
  /** The spot: restart location, or where the foul happened. */
  x: number;
  z: number;
  /** Sim tick it happened on. */
  tick: number;
}

export function createRefSignalFrame(): RefSignalFrame {
  return {
    kind: RefSignalWire.Invalid,
    phase: MatchPhase.Warmup,
    player: -1,
    team: -1,
    x: 0,
    z: 0,
    tick: 0,
  };
}

/** True for a kind this codec is willing to carry. */
export function isRefSignalKind(kind: number): boolean {
  return kind === RefSignalWire.Whistle || kind === RefSignalWire.Yellow || kind === RefSignalWire.Red;
}

/**
 * Write a RefereeSignal payload into `w` (no type byte — protocol.ts adds it).
 *
 * An out-of-range `kind` is written as-is rather than silently coerced: the
 * decoder rejects it, which surfaces a sender bug instead of shipping a wrong
 * card. The caller is expected to have a real signal in hand.
 */
export function encodeRefSignal(
  w: BitWriter,
  kind: RefSignalWire,
  tick: number,
  x: number,
  z: number,
  phase: MatchPhase,
  player: number,
  team: number,
): void {
  w.writeBits(kind & 0x7, REF_SIGNAL_KIND_BITS);
  w.writeBits(phase & 0xf, WIRE_PHASE_BITS);
  w.writeBits(encodePlayerRef(player), WIRE_PLAYER_REF_BITS);
  w.writeBits(encodeTeamRef(team), WIRE_TEAM_REF_BITS);
  w.writeBits(quantizePosX(x), QUANT_POS_BITS);
  w.writeBits(quantizePosZ(z), QUANT_POS_BITS);
  w.writeBits(tick >>> 0, WIRE_TICK_BITS);
}

/**
 * Read a RefereeSignal payload into `out`. Returns false — leaving `out.kind`
 * set to `Invalid` — for a truncated frame or an unknown kind, so a hostile or
 * corrupt frame can never make a client flash a card at nobody (§2.9).
 */
export function decodeRefSignal(r: BitReader, out: RefSignalFrame): boolean {
  const kind = r.readBits(REF_SIGNAL_KIND_BITS);
  const phase = r.readBits(WIRE_PHASE_BITS);
  const playerRef = r.readBits(WIRE_PLAYER_REF_BITS);
  const teamRef = r.readBits(WIRE_TEAM_REF_BITS);
  const qx = r.readBits(QUANT_POS_BITS);
  const qz = r.readBits(QUANT_POS_BITS);
  const tick = r.readBits(WIRE_TICK_BITS);
  if (r.overflow || !isRefSignalKind(kind)) {
    out.kind = RefSignalWire.Invalid;
    return false;
  }
  out.kind = kind as RefSignalWire;
  out.phase = phase as MatchPhase;
  out.player = decodePlayerRef(playerRef);
  out.team = decodeTeamRef(teamRef);
  out.x = dequantizePosX(qx);
  out.z = dequantizePosZ(qz);
  out.tick = tick >>> 0;
  return true;
}
